import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import { getActingCustomer } from '@/lib/actingContext';

/**
 * Browser Supabase client.
 *
 * - Reads VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY (typed in vite-env.d.ts).
 * - Anon key is public-safe; RLS in the `orders` schema enforces tenant
 *   isolation. The service role key NEVER appears in the client bundle —
 *   it's a Netlify function secret only.
 * - We crash early on missing env so misconfiguration shows up at boot
 *   rather than at the first auth call.
 * - A custom `fetch` stamps the per-tab acting-customer context onto every
 *   request as `x-acting-customer` (see docs/ACTING-CONTEXT.md). It's read
 *   fresh per request from sessionStorage, so switching customer in a tab
 *   takes effect immediately without recreating the client. The DB's
 *   current_customer_id() only honors the header after a membership/superadmin
 *   check, so this is a signal — never the security boundary.
 */
const url = import.meta.env.VITE_SUPABASE_URL;
const anonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!url || !anonKey) {
  throw new Error(
    'Missing Supabase env. Copy .env.example to .env.local and set ' +
      'VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY.',
  );
}

const actingFetch: typeof fetch = (input, init) => {
  const acting = getActingCustomer();
  if (!acting) return fetch(input, init);
  const headers = new Headers(init?.headers);
  headers.set('x-acting-customer', acting);
  return fetch(input, { ...init, headers });
};

export const supabase: SupabaseClient = createClient(url, anonKey, {
  auth: {
    persistSession: true,
    autoRefreshToken: true,
    detectSessionInUrl: false,
    // Email + password only for v1 — no magic links (operators on shared
    // shop computers, per CLAUDE.md auth flow).
    flowType: 'pkce',
  },
  global: { fetch: actingFetch },
});
